import { DeepReadonly } from 'utility-types';

export interface Document {
  id?: string;
}

// ------------------------------------------------------
export interface RelayConnection<T> {
  readonly totalCount?: number;
  readonly pageInfo?: IPageInfo;
  readonly edges?: ReadonlyArray<RelayEdge<T>>;
}

export interface RelayEdge<T> {
  readonly cursor?: string;
  readonly node?: DeepReadonly<T>;
}

// ------------------------------------------------------
export interface IBizMetrics {
  newSubscriptionAmount?: number;
  expansionAccountAmount?: number;
  expansionInboxAmount?: number;
  contractionAccountAmount?: number;
  contractionInboxAmount?: number;
  cancellationAmount?: number;
  pastDueAmount?: number;
  delinquentAmount?: number;
  paymentFailedAmount?: number;
  refundAmount?: number;
  reactivationAmount?: number;
  disputeAmount?: number;
}

// ------------------------------------------------------
export interface ISupport {
  arr?: number;
  mrr?: number;
  organizations?: IOrganization[];
  accounts?: IAccount[];
  billings?: IBilling[];
  inboxes?: IInbox[];
  logons?: ILogon[];
  bizMetrics?: IBizMetrics;
  orgActivityData?: JSON;
}

// ------------------------------------------------------
export interface IAccountConnection {
  totalCount?: number;
  pageInfo?: IPageInfo;
  edges?: IAccountEdge[];
}

// ------------------------------------------------------
export interface IAccountEdge {
  cursor?: string;
  node?: IAccount;
}

// ------------------------------------------------------
export interface IEmailVerificationProvider {
  id?: string;
  name?: string;
}

// ------------------------------------------------------
export interface IValue {
  value?: string;
  error?: string;
}

// ------------------------------------------------------
export interface IDailySendingInfo {
  quota?: number;
  sendCount?: number;
}

// ------------------------------------------------------
export interface IMutationData {
  type?: string;
  name?: string;
  count?: number;
  category?: string;
}

// ------------------------------------------------------
export interface ILogonData {
  logon?: ILogon;
  count?: number;
}

// ------------------------------------------------------
export interface IOrganizationConnection {
  totalCount?: number;
  pageInfo?: IPageInfo;
  edges?: IOrganizationEdge[];
}

// ------------------------------------------------------
export interface IOrganizationEdge {
  cursor?: string;
  node?: IOrganization;
}

// ------------------------------------------------------
export interface ILogonConnection {
  totalCount?: number;
  pageInfo?: IPageInfo;
  edges?: ILogonEdge[];
}

// ------------------------------------------------------
export interface ILogonEdge {
  cursor?: string;
  node?: IProfile;
}

// ------------------------------------------------------
export interface IInboxConnection {
  totalCount?: number;
  pageInfo?: IPageInfo;
  edges?: IInboxEdge[];
}

// ------------------------------------------------------
export interface IInboxEdge {
  cursor?: string;
  node?: IInbox;
}

// ------------------------------------------------------
export interface ISupportSearch {
  accounts?: IAccount[];
  organizations?: IOrganization[];
  logons?: ILogon[];
  inboxes?: IInbox[];
  billings?: IBilling[];
}

// ------------------------------------------------------
export interface IEventEntry {
  id: string;
  candidate_id: string;
  candidate_name: string;
  candidate_score?: number;
  avatar_url: string;
  events: IEvent[];
}

// ------------------------------------------------------
export interface IEvent {
  type: string;
  time: string;
  count: number;
  data?: IEventData;
}

// ------------------------------------------------------
export interface IEventData {
  funnel_id?: string;
  funnel_name?: string;
  link?: string;
  agent?: string;
}

// ------------------------------------------------------
export interface INotification {
  id: string;
  type: string;
  time: string;
  data?: INotificationData;
  options?: INotificationOptions;
  read?: boolean;
}

// ------------------------------------------------------
export interface INotificationData {
  candidate_id?: string;
  avatar_url?: string;
  candidate_name?: string;
  candidate_score?: number;
  funnel_id?: string;
  funnel_name?: string;
  link?: string;
  agent?: string;
  message?: string;
}

// ------------------------------------------------------
export interface INotificationOptions {
  play_sound?: boolean;
}

// ------------------------------------------------------
export interface IFunnelStepVariation {
  id?: string;
  type?: string;
  statement?: string;
  descriptionHtml?: string;
  descriptionExtract?: string;
  acceptBox?: boolean;
  required?: boolean;
  minSelection?: number;
  maxSelection?: number;
  randomized?: boolean;
  choices?: string;
  minScore?: number;
  manualReview?: boolean;
  explanation?: boolean;

  name?: string;
  subject?: string;
  body?: string;
  bodyExtract?: string;
  continueThread?: boolean;
  openTracking?: boolean;
  clickTracking?: boolean;
  asDraft?: boolean;
  cced?: string;
  unit?: string;
  value?: number;
  assignee?: number;
  waitForCompletion?: boolean;
  business?: boolean;
  targetUrl?: string;
  request?: string;
  params?: string;
  cookies?: string;
  payload?: string;
  hookUrl?: string;
  message?: string;
  text?: string;
  textMessage?: string;
  description?: string;
  cacheTotal?: number;
  cacheOpen?: number;
  cacheClick?: number;
  cacheReply?: number;
  cacheUnsub?: number;
  cacheBounce?: number;
}

// ------------------------------------------------------
export interface ISchedule {
  id?: number;
  triggerAt?: string;
  candidateCount?: number;
}

// ------------------------------------------------------
export interface IFunnelStep {
  id?: string;
  position?: number;

  variations?: IFunnelStepVariation[];
}

// ------------------------------------------------------
export interface IFunnel {
  all?: number;
  none?: number;
  pending?: number;
  ready?: number;
}

// ------------------------------------------------------
export interface IMessage {
  id?: string;
  subject?: string;
  body?: string;
  fromEmail?: string;
  fromName?: string;
  to?: string;
  cc?: string;
  date?: string;
}

// ------------------------------------------------------
export interface IDraft {
  id?: string;
  message?: IMessage;
  messageInfo?: JSON;
}

// ------------------------------------------------------
export interface IReview {
  id?: string;
  name?: string;
  instructions?: string;
  completedAt?: string;
  createdAt?: string;
  candidate?: ICandidate;
  funnel?: IFunnel;
  todo?: ITodo;
}

// ------------------------------------------------------
export interface INote {
  id?: string;
  content?: string;
  completedAt?: string;
  author?: ILogon;
}

// ------------------------------------------------------
export interface ITimeZone {
  id?: string;
  name?: string;
  offset?: string;
  offsetNow?: string;
  mappingValue?: string;
}

// ------------------------------------------------------
export interface ITimeRange {
  id?: string;
  wday?: number;
  fromMinute?: number;
  toMinute?: number;
}

// ------------------------------------------------------
export interface ITimeTable {
  id?: string;
  name?: string;
  timeZone?: string;

  timeRanges?: ITimeRange[];

  workingDays?: boolean[];
}

// ------------------------------------------------------
export interface IFunnelLink {
  id?: string;
  public?: boolean;
  organization?: IOrganization;
  funnel?: IFunnel;
  owner?: ILogon;
  createdAt?: string;
}

// ------------------------------------------------------
export interface IFunnel {
  id?: string;
  name?: string;
  description?: string;
  live?: boolean;
  archived?: boolean;
  applicationCount?: number;
  sharing?: number;
  // funnel?: IFunnel;
  inboxes?: IInbox[];
  inboxesAvailable?: IInbox[];
  timeTable?: ITimeTable;
  jobSpec?: string;
  jobSpecTitle?: string;

  applicationStarted?: number;
  cacheTotal?: number;
  cacheOpen?: number;
  cacheClick?: number;
  cacheReply?: number;
  cacheUnsub?: number;
  cacheBounce?: number;

  stats?: JSON;
  applicationStats?: JSON;
  statsSummary?: JSON;
  volumeStats?: JSON;
  allInboxes?: IInbox[];

  allowReenter?: boolean;
  addedToLibrary?: boolean;
  unsubscribeHeader?: boolean;
  cancelColleagues?: boolean;
  continueOnReply?: boolean;
  enableCaptureForm?: boolean;
  captureForm?: string;

  preview?: string;

  steps?: IFunnelStep[];
  stepCount?: number;
  funnelAttributes?: ICustomAttribute[];
}

// ------------------------------------------------------
export interface IApplicationStepInfo {
  id?: string; // ID
  response?: string;
  funnelStepVariation?: IFunnelStepVariation;
  createdAt?: string;
  updatedAt?: string;
}

// ------------------------------------------------------
export interface IApplication {
  id?: string; // ID
  stage?: number;
  stageName?: string;
  error?: string;
  processAt?: string;

  candidate?: ICandidate;
  sentiment?: number;
  labelId?: number;

  funnel?: IFunnel;

  currentStepVariation?: IFunnelStepVariation;
  applicationStepInfos?: IApplicationStepInfo[];
}

// ------------------------------------------------------
export interface IApplicationClickInfo {
  id?: string;
  count?: number;
  link?: string;
  application?: IApplication;
  createdAt?: string;
}

// ------------------------------------------------------
export interface ICandidate {
  id?: string; // ID
  hide?: boolean; // for UI only, not Database
  firstName?: string;
  lastName?: string;
  name?: string;
  email?: string;
  phone?: string;
  verified?: boolean;
  verifiedSource?: IEmailVerificationProvider;
  verifiedAt?: string;
  mxProvider?: string;
  avatarUrl?: string;
  isLead?: boolean;
  doNotContact?: boolean;
  attributes?: ICustomAttribute[];
  tags?: ITag[];
}

// ------------------------------------------------------
export interface ICandidateEdge {
  cursor?: string;
  node?: ICandidate;
}

// ------------------------------------------------------
export interface IProfileEdge {
  cursor?: string;
  node?: IProfile;
}

// ------------------------------------------------------
export interface ICompanyEdge {
  cursor?: string;
  node?: ICompany;
}

// ------------------------------------------------------
export interface IPageInfo {
  startCursor?: string;
  endCursor?: string;
  hasNextPage?: string;
  hasPreviousPage?: string;
}
// ------------------------------------------------------
export interface ICandidateConnection {
  totalCount?: number;
  pageInfo?: IPageInfo;
  edges?: ICandidateEdge[];
}

// ------------------------------------------------------
export interface ICompanyConnection {
  totalCount?: number;
  pageInfo?: IPageInfo;
  edges?: ICompanyEdge[];
}

// ------------------------------------------------------
export interface ICompany {
  id?: string;
  name?: string;
  domain?: string;
  avatarUrl?: string;

  candidates?: ICandidate[];
}

// ------------------------------------------------------
export interface IDnCDomain {
  id?: string;
  domain?: string;
  author?: string;
  createdAt?: string;
}

// ------------------------------------------------------
export interface ISpreadsheet {
  id?: string;
  name?: string;
  url?: string;
  owner?: string;
  changed_last?: string;
  error?: string; // for UI only, not Database
}

// ------------------------------------------------------
export enum InboxType {
  GMail = 'gmail',
  Outlook = 'outlook',
  Custom = 'custom',
}

// ------------------------------------------------------
export enum DriveType {
  GDrive = 'gmail',
  OneDrive = 'outlook',
  DRopBox = 'dropbox',
}

// ------------------------------------------------------
export interface IDeliverabilityItem {
  id?: string;
  providerType?: string;
  description?: string;
  folder?: string;
  category?: string;
  createdAt?: string;
  spfError?: string;
  spfWarning?: string;
  dkimError?: string;
  dkimWarning?: string;
}

// ------------------------------------------------------
export interface IInbox {
  id?: string;
  type?: string;
  name?: string;
  email?: string;
  sharing?: number;
  assigned?: boolean;
  accredited?: boolean;
  reGrantUrl?: string;
  spreadsheets?: ISpreadsheet[];
  deliverabilityItems?: IDeliverabilityItem;
  generateReply?: boolean;
  generateCandidates?: boolean;
  autoTestDeliverability?: boolean;
  autosync?: boolean;
  // addCandidateToFunnel?: IFunnel;
  paused?: boolean;
  canUnpause?: boolean;
  chartist?: any;
  canView?: boolean;
  signature?: string;
  bcc?: string;
  defaultDelay?: number;
  queueLockedFor?: number;
  smtpAddress?: string;
  smtpPort?: number;
  smtpLogin?: string;
  smtpPassword?: string;
  imapAddress?: string;
  imapPort?: number;
  imapLogin?: string;
  imapPassword?: string;
  dailySendingInfo?: IDailySendingInfo;
}

// ------------------------------------------------------
export interface IDrive {
  id?: string;
  type?: string;
  name?: string;
  email?: string;
  accredited?: boolean;
  reGrantUrl?: string;
  spreadsheets?: ISpreadsheet[];
  canView?: boolean;
}

// ------------------------------------------------------
export interface IAutoImport {
  id?: string;
  type?: string;
  name?: string;
  drive?: IDrive;
}

// ------------------------------------------------------
export interface ISubscription {
  id?: number;
  name?: string; // basic, pro, expired
  plan?: string; // stripe plan: next-basic-monthly
  cycle?: string; // monthly, annually, weekly...
  quantity?: number;
  costInCents?: number;
  expiredAt?: string;
  canceledAt?: string;
  status?: string;
}

// ------------------------------------------------------
export interface ICustomAttribute {
  id?: string;
  name?: string;
  icon?: string;
  attributeValueId?: string;
  value?: string;
  placeholder?: string;
}

// ------------------------------------------------------
export interface ILabel {
  id?: string;
  name?: string;
  icon?: string;
  sentiment?: number;
}

// ------------------------------------------------------
export interface IGroup {
  id?: string;
  name?: string;
  icon?: string;
  sharing?: number;
  rejectPreviouslyContacted?: boolean;
  candidatesCount?: number;
  emailVerificationProvider?: IEmailVerificationProvider;
}

// ------------------------------------------------------
export interface ITag {
  id?: string;
  name?: string;
  icon?: string;
  value?: boolean;
}

// ------------------------------------------------------
export interface ITodo {
  id?: string;
  name?: string;
  type?: string;
  message?: IMessage;
  messageInfo?: JSON;
  completedAt?: string;
  instructions?: string;
  repliedAt?: string;
  sentiment?: number;
  createdAt?: string;
  candidate?: ICandidate;
  funnel?: IFunnel;
  application?: IApplication;
  inbox?: IInbox;
}

// ------------------------------------------------------
export interface IAccount {
  id?: string;
  name?: string;
  customDomainTracking?: string;
  trackingDelayInSec?: number;
  timeZone?: string;
  openNotification?: boolean;
  openSound?: boolean;
  clickNotification?: boolean;
  clickSound?: boolean;
  colleaguesShareRole?: boolean;
  plan?: string;

  subscription?: ISubscription;
  expiredAt?: string;
  disabledAt?: string;

  stats?: JSON;

  hasInboxes?: boolean;
  hasCandidates?: boolean;
  hasFunnels?: boolean;
  hasApplications?: boolean;

  candidate?: ICandidate;
  applications?: IApplication[];
  draft?: IDraft;
  review?: IReview;

  company?: ICompany;

  candidates?: ICandidateConnection;

  companies?: ICompanyConnection[];

  funnel?: IFunnel;
  funnels?: IFunnel[];

  audits?: IAudit[];
  auditsMutationData?: IMutationData[];
  reviews?: RelayConnection<IReview>;
  todos?: RelayConnection<ITodo>;

  urlInboxGoogle?: string;
  urlInboxMicrosoft?: string;

  urlDriveGoogle?: string;

  totalDrives?: number;
  inboxes?: IInbox[];
  inbox?: IInbox;

  verificationTools?: IEmailVerificationProvider[];
  verifyEmailBeforeSending?: boolean;
  rejectRiskyEmails?: boolean;
  rejectUnknownEmails?: boolean;
  emailVerificationValidityInDays?: boolean;
  emailVerificationCredits?: number;
  briteverifyApi?: string;
  dropcontactApi?: string;
  dropcontactCredits?: IValue;
  hunterApi?: string;
  hunterCredits?: IValue;
  neverbounceApi?: string;
  neverbounceCredits?: IValue;
  zerobounceApi?: string;
  zerobounceCredits?: IValue;
  zapierApiKey?: string;
  twilioAccountSid?: string;
  twilioAuthToken?: string;
  twilioPhoneNumber?: string;
  webhookEnabled?: boolean;
  webhookOpenFirst?: boolean;
  webhookOpenRepeat?: boolean;
  webhookClickFirst?: boolean;
  webhookClickRepeat?: boolean;
  webhookReplyFirst?: boolean;
  webhookReplyRepeat?: boolean;
  webhookBounce?: boolean;
  webhookUnsubscribe?: boolean;
  webhookTag?: boolean;
  webhookTaskCompleted?: boolean;
  webhookApplicationCompleted?: boolean;
  webhookApplicationCheckpoint?: boolean;

  accountAttributes?: ICustomAttribute[];
  candidateAttributes?: ICustomAttribute[];
  companyAttributes?: ICustomAttribute[];
  inboxAttributes?: ICustomAttribute[];
  candidateTags?: ITag[];

  availableCandidates?: number;

  organization?: IOrganization;

  totalReviews?: number;

  chartist?: any;

  emailReportDeliveryTest?: string;
  bcc?: string;

  volumeStats?: JSON;
}

// ------------------------------------------------------
export interface IInvoice {
  id?: string;
  createdAt?: string;
  amountPaid?: number;
  description?: string;
  downloadLink?: string;
}

// ------------------------------------------------------
export interface IAudit {
  id?: string;
  author?: IProfile;
  name?: string;
  createdAt?: string;
  completedAt?: string;
  error?: string;
}

// ------------------------------------------------------
export interface IBilling {
  id?: string;
  ccCountry?: string;
  ccBrand?: string;
  ccExpMonth?: string;
  ccExpYear?: string;
  ccName?: string;
  ccLast4?: string;
  invoiceName?: string;
  invoiceCompanyName?: string;
  invoiceAddress1?: string;
  invoiceAddress2?: string;
  invoiceCity?: string;
  invoiceState?: string;
  invoiceZipCode?: string;
  invoiceCountry?: string;
  invoiceVat?: string;
  discountPercent?: number;
}

// ------------------------------------------------------
export interface IOrganization {
  id?: string;
  name?: string;
  domain?: string;
  emailReportDeliveryTest?: string;
  webhookUrl: string;
  orgtype?: string;

  accounts?: IAccount[];
  invoices?: IInvoice[];
  audits?: IAudit[];
  teamMembers?: IProfile[];
  permissions?: IPermission[];

  billing?: IBilling;
  emailVerificationCredits?: number;
}

// ------------------------------------------------------
export interface IPermission {
  id?: string;
  admin?: boolean;
  logon?: IProfile;
}

// ------------------------------------------------------
export interface IProfile {
  id?: number;
  name?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  avatarUrl?: string;
  admin?: string;

  organizations?: IOrganization[];
}

// ------------------------------------------------------
export interface ILogon {
  id?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  avatarUrl?: string;

  organizations?: IOrganization[];
}

// ------------------------------------------------------
export interface IAccountInboxInvite {
  id?: number;
  secret?: string;
  accountName?: string;
  fromName?: string;
  fromEmail?: string;
  fromAvatarUrl?: string;
  googleUrl?: string;
  microsoftUrl?: string;
  inboxEmail?: string;
}

// ------------------------------------------------------
export interface IOrganizationInvite {
  id?: number;
  secret?: string;
  accountName?: string;
  fromName?: string;
  fromEmail?: string;
  fromAvatarUrl?: string;
}

// ------------------------------------------------------
export interface IUtility {
  accountInboxInvite?: IAccountInboxInvite;
  organizationInvite?: IOrganizationInvite;
  timeZones?: ITimeZone[];
}
